import {type EventData, type EventMap, Listenable, type Notification} from "@juis/commons";
import type {DomEventName} from "./domEvents.js";
import {DomNode} from "./DomNode.js";
import {Property} from "./Property.js";
import {DIV, type HtmlTagName, SPAN} from "./tags.js";

export interface NavigationRequest {
    url: string;
    replace: boolean;
}

/** Every component speaks at least these events, so navigation and disposal are always typed. */
export interface ComponentEvents extends EventMap {
    destroy: Notification;
    requestNavigate: Notification<NavigationRequest>;
}

export interface ComponentOptions {
    tag?: HtmlTagName;
    cssClasses?: readonly string[];
}

export class Component<M extends ComponentEvents = ComponentEvents> extends Listenable<M> {
    readonly node: DomNode;
    readonly #pending: Promise<unknown>[] = [];
    #destroyed = false;

    constructor(options: ComponentOptions = {}) {
        super();
        this.node = new DomNode(options.tag ?? DIV, options.cssClasses ?? []);
    }

    get destroyed(): boolean {
        return this.#destroyed;
    }

    /** Declares a value that repaints this component when assigned. */
    property<TValue>(
        initialValue: TValue,
        paint: (value: TValue, previous: TValue) => void,
        paintNow = true
    ): Property<TValue> {
        return new Property(initialValue, paint, paintNow);
    }

    /** Handles a DOM event locally, without turning it into a framework event. */
    onDom<K extends DomEventName>(type: K, handler: (event: HTMLElementEventMap[K]) => void): () => void {
        return this.node.on(type, handler);
    }

    /**
     * Bridges a DOM event into the framework event chain. The mapper keeps the payload typed,
     * so components publish their own vocabulary rather than leaking DOM events upwards.
     */
    forwardDomEvent<K extends DomEventName, E extends keyof M & string>(
        domEventType: K,
        eventType: E,
        toEventData: (event: HTMLElementEventMap[K]) => EventData<M, E>
    ): () => void {
        return this.node.on(domEventType, event => this.trigger(eventType, toEventData(event)));
    }

    /** Asks an ancestor router to navigate. */
    navigate(url: string, options: { replace?: boolean } = {}): void {
        this.trigger("requestNavigate", {url, replace: options.replace ?? false});
    }

    /** Registers async work that {@link whenReady} waits for. */
    waitFor(promise: Promise<unknown>): void {
        this.#pending.push(promise);
    }

    async whenReady(): Promise<void> {
        while (this.#pending.length > 0) {
            const pending = [...this.#pending];
            this.#pending.length = 0;
            await Promise.all(pending);
        }
    }

    destroy(): void {
        if (this.#destroyed) {
            return;
        }
        this.#destroyed = true;
        this.trigger("destroy", undefined as EventData<M, "destroy">);
        this.removeAllListeners();
        this.nextListenable = undefined;
        this.node.destroy();
    }
}

export const createTextComponent = (text: string, tag: HtmlTagName = SPAN, cssClasses: readonly string[] = []): Component => {
    const component = new Component({tag, cssClasses});
    component.node.setText(text);
    return component;
};
